import { useEffect, useMemo, useState } from 'react'
import { Search } from 'lucide-react'
import { getStatementClasses, setStatementClass } from '../../api/accounting'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { BackLink, FilterChips } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { useSession } from '../../session'

const TYPES = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense']

// Where each account appears on the financial statements. Worked out
// automatically; an accountant can set any account's section here.
export default function StatementClasses() {
  const { notify } = useToast()
  const { readOnly } = useSession()
  const [data, setData] = useState(null)
  const [type, setType] = useState('Asset')
  const [search, setSearch] = useState('')
  const [saving, setSaving] = useState('')
  const [error, setError] = useState('')
  const [version, setVersion] = useState(0)

  useEffect(() => { getStatementClasses().then(setData).catch((e) => setError(e.message || 'Could not load the accounts.')) }, [version])

  const counts = useMemo(() => Object.fromEntries(TYPES.map((t) => [t, (data?.accounts || []).filter((a) => a.type === t).length])), [data])
  const shown = (data?.accounts || []).filter((a) => a.type === type && (!search.trim() || `${a.code} ${a.name}`.toLowerCase().includes(search.trim().toLowerCase())))
  const options = (data?.classes || []).filter((c) => c.type === type)

  const change = async (a, value) => {
    setSaving(a.code)
    setError('')
    try {
      await setStatementClass(a.code, value === 'auto' ? null : value)
      notify(value === 'auto' ? `${a.code} back to automatic` : `${a.code} moved to ${options.find((o) => o.key === value)?.title}`)
      setVersion((v) => v + 1)
    } catch (e) {
      setError(e.message || 'Could not change the section.')
    } finally {
      setSaving('')
    }
  }

  return (
    <div className="ci-page">
      <BackLink to="/accounting/reports" label="Reports" />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Account classification <HelpTip k="classification" /></h1>
            <div className="ci-title-meta">Where each account appears on the balance sheet and income statement.</div>
          </div>
        </div>
      </div>

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search accounts">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Account code or name…" />
        </label>
      </div>
      <FilterChips options={TYPES.map((t) => ({ id: t, label: t === 'Liability' ? 'Liabilities' : t === 'Equity' ? 'Equity' : `${t}s`, count: counts[t] }))} value={type} onChange={setType} />

      {error && <ErrorBanner message={error} />}

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table fit-sm">
            <thead><tr><th>Account</th><th>Statement section</th><th className="hide-sm">Why</th></tr></thead>
            <tbody>
              {!data ? (
                <tr><td colSpan="3"><Spinner label="Loading accounts…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="3"><div className="ci-empty">No accounts here.</div></td></tr>
              ) : shown.map((a) => (
                <tr key={a.code}>
                  <td><span className="ci-strong">{a.code} · {a.name}</span>{a.parent && <span className="cell-sub">In group {a.parent}</span>}</td>
                  <td>
                    <select className="ci-select fs-class-select" value={a.set_class || 'auto'} disabled={saving === a.code || readOnly} onChange={(e) => change(a, e.target.value)} aria-label={`Section for ${a.code}`}>
                      <option value="auto">Automatic: {a.set_class ? 'reset' : a.section}</option>
                      {options.map((o) => <option key={o.key} value={o.key}>{o.title}</option>)}
                    </select>
                  </td>
                  <td className="hide-sm ci-muted-cell">{a.source_label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
